
import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    empresa: '',
    mensagem: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3lf1pnod3remjjtglp0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nome: '',
          email: '',
          telefone: '',
          empresa: '',
          mensagem: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent('Olá! Gostaria de entrar em contato com a DataPrec para discutir uma possível parceria ou contratação de serviços. Estão disponíveis para conversar?');
    window.open(`https://wa.me/5531981041870?text=${message}`, '_blank');
  };

  const openWebsite = () => {
    window.open('https://www.dataprec.com', '_blank');
  };

  return (
    <section id="contato" className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-32 h-32 bg-blue-200 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-teal-200 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-purple-200 rounded-full blur-2xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <i className="ri-message-3-line"></i>
            Fale Conosco
          </div>
          <h2 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Entre em <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-teal-600">Contato</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Pronto para transformar seus dados em oportunidades? Fale conosco e descubra como podemos ajudar seu negócio
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 items-start">
          {/* Contact Information */}
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white/80 backdrop-blur-sm p-8 rounded-3xl shadow-xl border border-white/20">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-teal-500 rounded-lg flex items-center justify-center">
                  <i className="ri-contacts-line text-white text-sm"></i>
                </div>
                Informações de Contato
              </h3>
              
              <div className="space-y-6">
                <div className="group hover:bg-blue-50 p-4 rounded-2xl transition-all duration-300">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-white text-xl"></i>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 mb-1 text-lg">E-mail</h4>
                      <p className="text-gray-600 mb-2 font-medium">comercial@dataprec.com</p>
                      <span className="text-blue-600 hover:text-blue-700 font-semibold text-sm">
                        Enviar e-mail
                      </span>
                    </div>
                  </div>
                </div>

                <div className="group hover:bg-green-50 p-4 rounded-2xl transition-all duration-300 cursor-pointer" onClick={openWhatsApp}>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <i className="ri-whatsapp-line text-white text-xl"></i>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 mb-1 text-lg">WhatsApp</h4>
                      <p className="text-gray-600 mb-2 font-medium">(31) 98104-1870</p>
                      <span className="text-green-600 hover:text-green-700 font-semibold text-sm flex items-center gap-1">
                        Enviar mensagem <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="group hover:bg-purple-50 p-4 rounded-2xl transition-all duration-300">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-white text-xl"></i>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 mb-1 text-lg">Localização</h4>
                      <p className="text-gray-600 font-medium">Belo Horizonte, Brasil</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-500 to-teal-600 p-8 rounded-3xl text-white shadow-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-time-line text-white"></i>
                </div>
                <h4 className="font-bold text-xl">Horário de Atendimento</h4>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center bg-white/10 p-4 rounded-xl backdrop-blur-sm">
                  <span className="font-medium">Segunda a Sexta:</span>
                  <span className="font-bold">8h às 18h</span>
                </div>
                <div className="flex justify-between items-center bg-white/10 p-4 rounded-xl backdrop-blur-sm">
                  <span className="font-medium">Sábado e Domingo:</span>
                  <span className="font-bold">Fechado</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-white/90 backdrop-blur-sm p-10 rounded-3xl shadow-2xl border border-white/20">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Solicite uma Demonstração</h3>
                <p className="text-gray-600">Preencha o formulário e entraremos em contato em breve</p>
              </div>
              
              <form onSubmit={handleSubmit} data-readdy-form id="contato-form" className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="group">
                    <label htmlFor="nome" className="block text-sm font-semibold text-gray-700 mb-3 group-focus-within:text-blue-600 transition-colors">
                      <i className="ri-user-line mr-2"></i>Nome Completo
                    </label>
                    <input
                      type="text"
                      id="nome"
                      name="nome"
                      value={formData.nome}
                      onChange={handleInputChange}
                      className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300 text-sm bg-white/50 backdrop-blur-sm hover:border-gray-300"
                      placeholder="Seu nome completo"
                    />
                  </div>
                  
                  <div className="group">
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-3 group-focus-within:text-blue-600 transition-colors">
                      <i className="ri-mail-line mr-2"></i>E-mail
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300 text-sm bg-white/50 backdrop-blur-sm hover:border-gray-300"
                      placeholder="seu@email.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="group">
                    <label htmlFor="telefone" className="block text-sm font-semibold text-gray-700 mb-3 group-focus-within:text-blue-600 transition-colors">
                      <i className="ri-phone-line mr-2"></i>Telefone
                    </label>
                    <input
                      type="tel"
                      id="telefone"
                      name="telefone"
                      value={formData.telefone}
                      onChange={handleInputChange}
                      className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300 text-sm bg-white/50 backdrop-blur-sm hover:border-gray-300"
                      placeholder="(31) 99999-9999"
                    />
                  </div>
                  
                  <div className="group">
                    <label htmlFor="empresa" className="block text-sm font-semibold text-gray-700 mb-3 group-focus-within:text-blue-600 transition-colors">
                      <i className="ri-building-line mr-2"></i>Empresa
                    </label>
                    <input
                      type="text"
                      id="empresa"
                      name="empresa"
                      value={formData.empresa}
                      onChange={handleInputChange}
                      className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300 text-sm bg-white/50 backdrop-blur-sm hover:border-gray-300"
                      placeholder="Nome da empresa"
                    />
                  </div>
                </div>

                <div className="group">
                  <label htmlFor="mensagem" className="block text-sm font-semibold text-gray-700 mb-3 group-focus-within:text-blue-600 transition-colors">
                    <i className="ri-message-3-line mr-2"></i>Mensagem
                  </label>
                  <textarea
                    id="mensagem"
                    name="mensagem"
                    value={formData.mensagem}
                    onChange={handleInputChange}
                    rows={5}
                    maxLength={500}
                    className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300 text-sm resize-none bg-white/50 backdrop-blur-sm hover:border-gray-300"
                    placeholder="Conte-nos sobre suas necessidades e como podemos ajudar..."
                  ></textarea>
                  <div className="text-xs text-gray-500 mt-2 text-right">
                    {formData.mensagem.length}/500 caracteres
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-blue-600 to-teal-600 text-white py-4 px-8 rounded-2xl font-bold text-lg hover:from-blue-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 cursor-pointer whitespace-nowrap shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Enviando...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-send-plane-line"></i>
                      Enviar Mensagem
                    </span>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-6 animate-fade-in">
                    <div className="flex items-center justify-center">
                      <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-check-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-green-800 mb-1">Mensagem enviada com sucesso!</h4>
                        <p className="text-green-700">Entraremos em contato em breve.</p>
                      </div>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-200 rounded-2xl p-6 animate-fade-in">
                    <div className="flex items-center justify-center">
                      <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-error-warning-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-red-800 mb-1">Erro ao enviar mensagem</h4>
                        <p className="text-red-700">Tente novamente ou entre em contato via WhatsApp.</p>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
